import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { PsfColors } from '../theme';

export type PsfMode = 'compare' | 'benchmark';

interface StartWizardProps {
  onPickMode: (mode: PsfMode) => void;
  c: PsfColors;
}

function ChoiceCard({
  icon, title, subtitle, bullets, accent, onClick, c,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  bullets: string[];
  accent: string;
  onClick: () => void;
  c: PsfColors;
}) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        width: { xs: '100%', sm: 320 },
        p: 3,
        cursor: 'pointer',
        bgcolor: c.surfaceCard,
        border: `1px solid ${c.border}`,
        borderRadius: 3,
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: accent,
          boxShadow: `0 8px 28px ${accent}33`,
          '& .choice-icon': { transform: 'scale(1.12)' },
        },
      }}
    >
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, bgcolor: accent }} />
      <Box
        className="choice-icon"
        sx={{
          width: 56, height: 56, borderRadius: 2.5,
          bgcolor: `${accent}1e`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mb: 2, transition: 'transform 0.2s ease',
          color: accent,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: c.text, mb: 0.5 }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.8rem', color: c.textMuted, mb: 1.5, lineHeight: 1.5 }}>{subtitle}</Typography>
      {bullets.map((b) => (
        <Box key={b} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: accent, mt: '7px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.72rem', color: c.textMuted, lineHeight: 1.5 }}>{b}</Typography>
        </Box>
      ))}
    </Paper>
  );
}

export default function StartWizard({ onPickMode, c }: StartWizardProps) {
  return (
    <Box
      sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        p: 3, gap: 3, overflow: 'auto',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: c.text, letterSpacing: 0.2 }}>
          What would you like to do today?
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: c.textMuted, mt: 0.5 }}>
          Pick a starting point — you can restart anytime
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
        <ChoiceCard
          icon={<QueryStatsIcon sx={{ fontSize: 30 }} />}
          title="Compare Transactions"
          subtitle="Browse and slice the past transactions — no adjustments, just the raw market."
          bullets={[
            'Past 1 year shown by default',
            'Unit view and project view (avg PSF)',
            'Filter by date, type, tenure, sale type, bedrooms, price, PSF, size & floor',
          ]}
          accent={c.navyLight}
          onClick={() => onPickMode('compare')}
          c={c}
        />
        <ChoiceCard
          icon={<CompareArrowsIcon sx={{ fontSize: 30 }} />}
          title="Benchmark with New Launch"
          subtitle="Adjust resale PSF up to its brand-new equivalent, then compare against new-launch pricing."
          bullets={[
            'Tenure depreciation with a fully adjustable curve',
            'Physical & functional depreciation by age band',
            'Tap a Personalized PSF to see the top 5 cheaper new-launch units',
          ]}
          accent={c.orange}
          onClick={() => onPickMode('benchmark')}
          c={c}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.7 }}>
        <ApartmentIcon sx={{ fontSize: 14, color: c.textMuted }} />
        <Typography sx={{ fontSize: '0.68rem', color: c.textMuted }}>
          Private residential transactions · URA caveats via SuperApp
        </Typography>
      </Box>
    </Box>
  );
}
